<script lang="ts">
  import { onMount } from 'svelte';
  import { appStore } from '../stores/appStore';
  import { AppApiClient } from '../api_contracts/app.client';

  // Props
  let { repositoryName, ownerId }: { repositoryName: string; ownerId: string } = $props();

  // State
  let client: AppApiClient | null = $state(null);
  let token: string = $state('');
  let members: any[] = $state([]);
  let loading = $state(true);
  let error = $state('');

  // Add member form state
  let showAddMember = $state(false);
  let newMemberEmail = $state('');
  let newMemberRole = $state('write');
  let adding = $state(false);
  let addError = $state('');

  // Edit member state
  let editingMemberId: string | null = $state(null);

  // Subscribe to store
  appStore.subscribe((state) => {
    client = state.client;
    token = state.token;
  });

  async function fetchMembers() {
    if (!client || !token) return;

    try {
      loading = true;
      error = '';

      const result = await client.getRepositoryMembers({
        token,
        ownerId,
        repositoryName,
      });

      if (result.response.successFull) {
        members = result.response.members || [];
      } else {
        error = result.response.reason || 'Failed to fetch members';
      }
    } catch (e: any) {
      error = e.message || 'Error fetching members';
      console.error('Error fetching members:', e);
    } finally {
      loading = false;
    }
  }

  async function addMember() {
    if (!client || !token || !newMemberEmail) return;

    try {
      adding = true;
      addError = '';

      const result = await client.addRepositoryMember({
        token,
        ownerId,
        repositoryName,
        userEmail: newMemberEmail,
        role: newMemberRole,
      });

      if (result.response.successFull) {
        members = [result.response.member, ...members];
        newMemberEmail = '';
        newMemberRole = 'write';
        showAddMember = false;
      } else {
        addError = result.response.reason || 'Failed to add member';
      }
    } catch (e: any) {
      addError = e.message || 'Error adding member';
      console.error('Error adding member:', e);
    } finally {
      adding = false;
    }
  }

  async function updateMemberRole(memberId: string, newRole: string) {
    if (!client || !token) return;

    try {
      const result = await client.updateMemberRole({
        token,
        memberId,
        newRole,
      });

      if (result.response.successFull) {
        members = members.map(m =>
          m.id === memberId ? { ...m, role: newRole } : m
        );
        editingMemberId = null;
      } else {
        error = result.response.reason || 'Failed to update role';
      }
    } catch (e: any) {
      error = e.message || 'Error updating role';
      console.error('Error updating role:', e);
    }
  }

  async function removeMember(memberId: string) {
    if (!client || !token) return;
    if (!confirm('Are you sure you want to remove this member?')) return;

    try {
      const result = await client.removeRepositoryMember({
        token,
        memberId,
      });

      if (result.response.successFull) {
        members = members.filter(m => m.id !== memberId);
      } else {
        error = result.response.reason || 'Failed to remove member';
      }
    } catch (e: any) {
      error = e.message || 'Error removing member';
      console.error('Error removing member:', e);
    }
  }

  function getRoleBadgeColor(role: string): string {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'write':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'read':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  }

  onMount(() => {
    fetchMembers();
  });
</script>

<div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-600">
  <!-- Header -->
  <div class="px-6 py-4 border-b border-gray-300 dark:border-gray-600 flex justify-between items-center">
    <div>
      <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">Repository Members</h3>
      <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">
        Manage who has access to this repository
      </p>
    </div>
    <button
      onclick={() => showAddMember = !showAddMember}
      class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
    >
      Add Member
    </button>
  </div>

  <!-- Add Member Form -->
  {#if showAddMember}
    <div class="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-300 dark:border-gray-600">
      <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Add New Member</h4>

      {#if addError}
        <div class="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <p class="text-sm text-red-600 dark:text-red-400">{addError}</p>
        </div>
      {/if}

      <div class="flex gap-3">
        <input
          type="email"
          bind:value={newMemberEmail}
          placeholder="User email"
          class="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <select
          bind:value={newMemberRole}
          class="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="read">Read</option>
          <option value="write">Write</option>
          <option value="admin">Admin</option>
        </select>

        <button
          onclick={addMember}
          disabled={adding || !newMemberEmail}
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {adding ? 'Adding...' : 'Add'}
        </button>

        <button
          onclick={() => { showAddMember = false; addError = ''; newMemberEmail = ''; }}
          class="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-md transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  {/if}

  <!-- Error Message -->
  {#if error}
    <div class="px-6 py-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
      <p class="text-sm text-red-600 dark:text-red-400">{error}</p>
    </div>
  {/if}

  <!-- Members List -->
  <div class="divide-y divide-gray-200 dark:divide-gray-700">
    {#if loading}
      <div class="px-6 py-8 text-center">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading members...</p>
      </div>
    {:else if members.length === 0}
      <div class="px-6 py-8 text-center">
        <p class="text-sm text-gray-600 dark:text-gray-400">No members added yet</p>
      </div>
    {:else}
      {#each members as member}
        <div class="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
          <div class="flex items-center gap-3 flex-1">
            {#if member.avatar}
              <img src={member.avatar} alt={member.username} class="w-10 h-10 rounded-full" />
            {:else}
              <div class="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                {member.username.substring(0, 2).toUpperCase()}
              </div>
            {/if}

            <div class="flex-1">
              <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{member.username}</p>
              <p class="text-xs text-gray-500 dark:text-gray-400">{member.email}</p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            {#if editingMemberId === member.id}
              <select
                onchange={(e) => updateMemberRole(member.id, e.target.value)}
                value={member.role}
                class="px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="read">Read</option>
                <option value="write">Write</option>
                <option value="admin">Admin</option>
              </select>
              <button
                onclick={() => editingMemberId = null}
                class="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-md transition-colors"
              >
                Cancel
              </button>
            {:else}
              <button
                onclick={() => editingMemberId = member.id}
                class="px-3 py-1.5 border rounded-md text-xs font-medium transition-colors {getRoleBadgeColor(member.role)}"
              >
                {member.role}
              </button>

              <button
                onclick={() => removeMember(member.id)}
                class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                title="Remove member"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            {/if}
          </div>
        </div>
      {/each}
    {/if}
  </div>
</div>
